import moment from "moment-timezone";
import { useState, useMemo, useRef, useEffect, ReactNode } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
// hooks
import { useUser } from "contexts/UserContext";
// constants
import messages from "messages";
// utils
import getClasses from "utils/getClasses";
// styles
import styles from "./AutoSavedState.module.scss";
import { INITIAL_RETRY } from "hooks/useRetryFn";
import { MAX_AUTO_SAVE_RETRY } from "constants/maxAutoSaveRetry";

const VISITED_SESSION_KEY = "VISITED_URLS";

const CALENDAR_SETTINGS = Object.freeze({
  sameDay: "[today] [at] h:mmA z",
  lastDay: "[yesterday] [at] h:mmA z",
  sameElse: "DD/MM/YYYY [at] h:mmA z",
} as const);

type Props = {
  busy: boolean;
  /**
   * relects the saved state in current session
   * if no changes has been saved in the session, it should be undefined
   * @default undefined
   */
  saved?: boolean;
  retryCount: number;
  maxRetry?: number;
  className?: string;
  tooltipClassName?: string;
  tooltip?: ReactNode;
};

const AutoSavedState = ({
  busy,
  saved,
  retryCount,
  maxRetry = MAX_AUTO_SAVE_RETRY,
  className,
  tooltipClassName,
  tooltip,
}: Props) => {
  const { defaultUserTimezone, user } = useUser();
  const lastMessageRef = useRef("");
  const [visited, setVisited] = useState(false);

  useEffect(() => {
    let { id, urls } = JSON.parse(
      window.sessionStorage.getItem(VISITED_SESSION_KEY) || "{}",
    );
    if (id !== user?.sub) {
      window.sessionStorage.removeItem(VISITED_SESSION_KEY);
      id = undefined;
      urls = [];
    }

    setVisited(urls.some((el) => el === window.location.href));

    if (user?.sub) {
      urls.push(window.location.href);
      window.sessionStorage.setItem(
        VISITED_SESSION_KEY,
        JSON.stringify({
          id: user.sub,
          urls: urls,
        }),
      );
    }
    return () => {
      window.sessionStorage.removeItem(VISITED_SESSION_KEY);
    };
  }, [user?.sub]);

  const message = useMemo(() => {
    if (busy) {
      if (retryCount > INITIAL_RETRY) {
        return messages.failedToSaveChangesRetrying;
      }
      return messages.savingDraft;
    }
    // if not saved in the session, should always display: "All changes saved as draft"
    if (saved === undefined) {
      if (!visited) {
        return "";
      }
      return messages.allChangesSavedAsDraft;
    }

    if (saved === false) {
      if (retryCount === INITIAL_RETRY) {
        return lastMessageRef.current;
      }
      if (retryCount >= maxRetry + INITIAL_RETRY - 1) {
        return messages.unableToSave;
      }
      return messages.failedToSaveChangesRetrying;
    }

    return (
      messages.draftSaved +
      moment.tz(Date.now(), defaultUserTimezone).calendar(CALENDAR_SETTINGS)
    );
  }, [busy, retryCount, saved, defaultUserTimezone, maxRetry, visited]);

  useEffect(() => {
    lastMessageRef.current = message;
  }, [message]);

  return (
    <OverlayTrigger
      placement={"bottom"}
      overlay={
        <Tooltip id="auto-saved-tooltip" className={tooltipClassName}>
          {tooltip}
        </Tooltip>
      }
    >
      <span className={getClasses(styles["auto-saved-state"], className)}>
        {message}
      </span>
    </OverlayTrigger>
  );
};

export default AutoSavedState;
